import type { IntlKey } from 'locales';
import { AddonAction } from 'models/AddonAction';
import { TradeWay } from 'models/TradeWay';
import type { RemunerationMode, SolveMode } from 'state/addonForm';
import type { QuoteWorkspaceType } from 'state/workspaces/model';

export type FlowWay = 'NoFlowWay' | 'YouReceiveSgPaysYou' | 'YouPaySgReceives';

export const getAddonFlowWayNext = (
  solveMode: SolveMode,
  solveResult: number,
  remunerationMode?: RemunerationMode,
  addonAction?: AddonAction,
): FlowWay => {
  if (solveMode !== 'OfferPrice' && solveMode !== 'UpfrontFee' && solveMode !== 'FixedAmount') {
    return 'NoFlowWay';
  }

  if (addonAction === 'Increase' && remunerationMode === 'Upfront' && solveResult > 0) {
    return 'YouReceiveSgPaysYou';
  }

  if (addonAction === 'Increase' && remunerationMode === 'Upfront' && solveResult < 0) {
    return 'YouPaySgReceives';
  }

  if (addonAction === 'Increase' && remunerationMode === 'Reoffer' && solveResult > 0) {
    return 'YouPaySgReceives';
  }

  if (addonAction === 'Increase' && remunerationMode === 'Reoffer' && solveResult < 0) {
    return 'YouReceiveSgPaysYou';
  }

  if (addonAction === 'Increase' && remunerationMode === 'Premium' && solveResult > 0) {
    return 'YouReceiveSgPaysYou';
  }

  if (addonAction === 'Increase' && remunerationMode === 'Premium' && solveResult < 0) {
    return 'YouPaySgReceives';
  }

  if (addonAction === 'Decrease' && remunerationMode === 'Upfront' && solveResult > 0) {
    return 'YouPaySgReceives';
  }

  if (addonAction === 'Decrease' && remunerationMode === 'Upfront' && solveResult < 0) {
    return 'YouReceiveSgPaysYou';
  }

  if (addonAction === 'Decrease' && remunerationMode === 'Reoffer' && solveResult > 0) {
    return 'YouReceiveSgPaysYou';
  }

  if (addonAction === 'Decrease' && remunerationMode === 'Reoffer' && solveResult < 0) {
    return 'YouPaySgReceives';
  }

  if (addonAction === 'Decrease' && remunerationMode === 'Premium' && solveResult > 0) {
    return 'YouPaySgReceives';
  }

  if (addonAction === 'Decrease' && remunerationMode === 'Premium' && solveResult < 0) {
    return 'YouReceiveSgPaysYou';
  }

  return 'NoFlowWay';
};

export const getQuoteFlowWay = (
  workspaceType: QuoteWorkspaceType,
  pricerToBookingInvertedFlow: boolean,
  solvingMode?: string,
  remunerationMode?: string,
  solveResult?: number,
  tradeWay: TradeWay = 'Sell',
): FlowWay => {
  const isSolvingForPrice = () => {
    const upperCaseSolvingMode = solvingMode?.toUpperCase();
    switch (workspaceType) {
      case 'dynamic':
        return upperCaseSolvingMode === 'PRICE';
      case 'static':
        return (
          upperCaseSolvingMode === 'UPFRONTFEE' ||
          upperCaseSolvingMode === 'OFFERPRICE' ||
          upperCaseSolvingMode === 'FIXEDAMOUNT' ||
          upperCaseSolvingMode === 'SGPRICE'
        );
      default:
        return false;
    }
  };

  if (!isSolvingForPrice() || solveResult === undefined || remunerationMode === undefined) {
    return 'NoFlowWay';
  }

  return getCashFlowWay(solveResult, remunerationMode, tradeWay, pricerToBookingInvertedFlow);
};

export function getCashFlowWay(
  price: number,
  remunerationMode: string,
  tradeWay: TradeWay,
  pricerToBookingInvertedFlow: boolean,
): FlowWay {
  let cashFlowWay: FlowWay = 'NoFlowWay';

  if (price > 0 && remunerationMode === 'Reoffer') {
    cashFlowWay = 'YouPaySgReceives';
  }

  if (price > 0 && remunerationMode === 'Upfront') {
    cashFlowWay = 'YouReceiveSgPaysYou';
  }

  if (price > 0 && remunerationMode === 'Premium') {
    cashFlowWay = 'YouReceiveSgPaysYou';
  }

  if (price < 0 && remunerationMode === 'Reoffer') {
    cashFlowWay = 'YouReceiveSgPaysYou';
  }

  if (price < 0 && remunerationMode === 'Upfront') {
    cashFlowWay = 'YouPaySgReceives';
  }

  if (price < 0 && remunerationMode === 'Premium') {
    cashFlowWay = 'YouPaySgReceives';
  }

  if (pricerToBookingInvertedFlow) {
    cashFlowWay = swapCashFlowWay(cashFlowWay);
  }

  return tradeWay === 'Sell' ? cashFlowWay : swapCashFlowWay(cashFlowWay);
}

export function getFlowWayTranslationKey(way: FlowWay): IntlKey {
  switch (way) {
    case 'YouReceiveSgPaysYou':
      return 'flowSense.YouReceiveSgPaysYou';
    case 'YouPaySgReceives':
      return 'flowSense.YouPaySgReceives';
    default:
      return way as any;
  }
}

function swapCashFlowWay(way: FlowWay): FlowWay {
  if (way === 'NoFlowWay') {
    return 'NoFlowWay';
  }

  return way === 'YouPaySgReceives' ? 'YouReceiveSgPaysYou' : 'YouPaySgReceives';
}
